import React, { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Box,
  Button,
  Stack,
  Divider,
  Typography,
  IconButton,
  Grid,
} from '@mui/material'
import ScrollableImageContainer from '../ScrollableContainer'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import {
  convertTo3DAsync,
  analyzeFilesAsync,
  convertIfcAsync,
  convertDxfAsync,
} from '../../services/filesService'
import LoadingComponent from '../DeterminateLoader'
import {
  ConvertFailedErrorType,
  DrawingType,
  FileType,
} from '../../models/File'
import {
  addPage,
  clearAnalyzeResults,
  clearState,
  removePage,
  selectFile,
  selectPage,
  setConvertFailedError,
  setIsLoading,
} from '../../actions/files'
import UploadFiles from './UploadFiles'
import ReplaceblePages from '../ReplaceblePages'
import TabComponent from '../TabComponent'
import TabContent from '../TabContent'
import {
  ElevationsIcon,
  FloorplansIcon,
  GeoReportIcon,
  SpacialLogo,
  SurveyIcon,
} from '../Svgs'
import { useNavigate } from 'react-router-dom'
import UploadConvertIssuesComponent from './UploadConvertIssuesComponent'
import { createSelector } from '@reduxjs/toolkit'
import ConvertFailedModalComponent from './ConvertFailedModalComponent'

const icons = [
  {
    icon: <FloorplansIcon />,
    name: 'Floorplans',
  },
  {
    icon: <ElevationsIcon />,
    name: 'Elevations',
  },
  {
    icon: <SurveyIcon />,
    name: 'Survey',
  },
  {
    icon: <GeoReportIcon />,
    name: 'Geo Report',
  },
]

const ModalBackground = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100% - 52px)',
        top: '52px',
        background: '#FFF',
        position: 'absolute',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          left: '35%',
          top: '75%',
          position: 'absolute',
          width: '1250px',
          height: '1048px',
          transform: 'rotate(-48.266deg)',
          borderRadius: '1250px',
          backgroundColor: '#CDF765',
          filter: 'blur(300px)',
        }}
      />

      <Box
        sx={{
          left: '75%',
          top: '-10%',
          position: 'absolute',
          width: '818px',
          height: '1280px',
          transform: 'rotate(-30deg)',
          borderRadius: '1280px',
          backgroundColor: '#E5F0FF',
          filter: 'blur(200px)',
        }}
      />

      <Box
        sx={{
          left: '0%',
          top: '-25%',
          position: 'absolute',
          width: '1196px',
          height: '1048px',
          transform: 'rotate(-30.206deg)',
          borderRadius: '1196px',
          backgroundColor: '#1705E5',
          filter: 'blur(350px)',
        }}
      />

      <Box
        sx={{
          left: '20%',
          top: '-50%',
          position: 'absolute',
          width: '818px',
          height: '1280px',
          transform: 'rotate(-30deg)',
          borderRadius: '1280px',
          backgroundColor: '#E5F0FF',
          filter: 'blur(200px)',
        }}
      />

      <Box
        sx={{
          left: '-10%',
          top: '0%',
          position: 'absolute',
          width: '549px',
          height: '100%',
          borderRadius: '100%',
          backgroundColor: '#6FCCDD66',
          filter: 'blur(200px)',
        }}
      />
    </Box>
  )
}

const ErrorPage = () => {
  return (
    <Box
      gap={'5px'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pt: '20px',
      }}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='15'
        viewBox='0 0 16 15'
        fill='none'
      >
        <path
          d='M8 5.69635V8.58458M8 11.4728H8.00682M7.0556 2.00789L1.44595 12.2659C1.13481 12.8348 0.979233 13.1193 1.00223 13.3528C1.02228 13.5565 1.12307 13.7415 1.2795 13.8619C1.45884 14 1.76935 14 2.39035 14H13.6096C14.2307 14 14.5412 14 14.7205 13.8619C14.8769 13.7415 14.9777 13.5565 14.9978 13.3528C15.0208 13.1193 14.8652 12.8348 14.554 12.2659L8.9444 2.00789C8.63437 1.44096 8.47935 1.15749 8.27711 1.06228C8.1007 0.979239 7.8993 0.979239 7.72289 1.06228C7.52065 1.15749 7.36563 1.44096 7.0556 2.00789Z'
          stroke='#FF2E79'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
      <Typography
        sx={{
          color: '#04002E',
          fontSize: '12px',
          lineHeight: 'normal',
        }}
      >
        Convert to 3D failed. Please try again.
      </Typography>
    </Box>
  )
}

const convertIssuesSelector = createSelector(
  [(state: RootState) => state.drawings],
  (drawings) => drawings.convertResult?.issues,
)

const convertFailedErrorSelector = createSelector(
  [(state: RootState) => state.drawings],
  (drawings) => drawings.convertFailedError,
)

interface ConvertContentProps {
  type: DrawingType
  isFailed: boolean
  goBackHandle: () => void
}

const ConvertContent: React.FC<ConvertContentProps> = ({
  type,
  isFailed,
  goBackHandle,
}) => {
  const dispatch = useDispatch<any>()
  const [checkePageId, setcheckePageId] = useState<string>('')
  const drawings = useSelector((state: RootState) => state.drawings)
  const selectedPageId = drawings.selectedPageId
  const selectedFileUnrecognizedPages =
    drawings.unrecognizedPages.find(
      (p) => p.fileName === drawings.selectedFileName,
    )?.pages ?? []
  const floorplans = drawings.recognizedPages.filter(
    (page) => page.drawingType === DrawingType.Floorplan,
  )
  const elevations = drawings.recognizedPages.filter(
    (page) => page.drawingType === DrawingType.Elevation,
  )
  const pages = type === DrawingType.Floorplan ? floorplans : elevations

  const addPageToAnalyzeResultHandle = () => {
    dispatch(addPage({ pageId: selectedPageId, type }))
    setcheckePageId('')
  }

  const removePageFromAnalyzeResultHandle = (pageId: string) => {
    dispatch(removePage({ pageId }))
  }

  const onPreviousFileClick = () => {
    const selectedFileIndex = drawings.unrecognizedPages.findIndex(
      (file) => file.fileName === drawings.selectedFileName,
    )
    if (selectedFileIndex !== -1) {
      const length = drawings.unrecognizedPages.length
      const previuosFileIndex = (selectedFileIndex - 1 + length) % length
      dispatch(
        selectFile({
          fileName: drawings.unrecognizedPages[previuosFileIndex].fileName,
        }),
      )
    }
  }

  const onNextFileClick = () => {
    const selectedFileIndex = drawings.unrecognizedPages.findIndex(
      (file) => file.fileName === drawings.selectedFileName,
    )
    if (selectedFileIndex !== -1) {
      const length = drawings.unrecognizedPages.length
      const nextFileIndex = (selectedFileIndex + 1) % length
      dispatch(
        selectFile({
          fileName: drawings.unrecognizedPages[nextFileIndex].fileName,
        }),
      )
    }
  }

  const handleImageClick = (pageId: string) => {
    dispatch(selectPage({ pageId }))
  }

  const handleCheckBoxClick = (pageId: string) => {
    setcheckePageId(pageId)
  }

  return (
    <Stack direction='column' sx={{ gap: 1, height: '100%' }}>
      {isFailed ? (
        <ErrorPage />
      ) : (
        <>
          <ReplaceblePages
            title=''
            pages={pages}
            newPageId={checkePageId}
            addPageHandle={addPageToAnalyzeResultHandle}
            removePageHandle={removePageFromAnalyzeResultHandle}
            goBackHandle={goBackHandle}
          ></ReplaceblePages>
          {drawings.unrecognizedPages.length ? (
            <Box
              sx={{
                backgroundColor: '#000435',
                height: '65%',
                flexDirection: 'row',
                display: 'flex',
                padding: '11px 14px',
                paddingTop: '0px',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '60%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: '17px',
                    mt: '6px',
                  }}
                >
                  <IconButton
                    disableRipple
                    onClick={onPreviousFileClick}
                    sx={{
                      p: 0,
                      visibility:
                        drawings.files.length < 2 ? 'hidden' : 'visible',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='7'
                      height='12'
                      viewBox='0 0 7 12'
                      fill='none'
                    >
                      <path
                        d='M5.8418 1L0.9998 5.842L5.8418 10.684'
                        stroke='white'
                        strokeMiterlimit='10'
                      />
                    </svg>
                  </IconButton>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontSize: '14px',
                      lineHeight: 'normal',
                    }}
                  >
                    {drawings.selectedFileName}
                  </Typography>
                  <IconButton
                    disableRipple
                    onClick={onNextFileClick}
                    sx={{
                      p: 0,
                      visibility:
                        drawings.files.length < 2 ? 'hidden' : 'visible',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='7'
                      height='12'
                      viewBox='0 0 7 12'
                      fill='none'
                    >
                      <path
                        d='M0.841797 11L5.68379 6.158L0.841797 1.31601'
                        stroke='white'
                        strokeMiterlimit='10'
                      />
                    </svg>
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    height: 'calc(100% - 51px)',
                  }}
                >
                  <Box sx={{ width: '80%' }}>
                    <img
                      alt='Recognized floor'
                      src={
                        selectedFileUnrecognizedPages?.filter(
                          (page) => page.id === selectedPageId,
                        )[0]
                          ? selectedFileUnrecognizedPages?.filter(
                              (page) => page.id === selectedPageId,
                            )[0]?.image
                          : selectedFileUnrecognizedPages![0].image
                      }
                      style={{
                        height: '100%',
                        objectFit: 'fill',
                        width: '100%',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      height: '100%',
                      width: '20%',
                    }}
                  >
                    <ScrollableImageContainer
                      pages={selectedFileUnrecognizedPages}
                      onImageClick={handleImageClick}
                      checkBox={{
                        showCheckbox: true,
                        onImageCheckBoxClick: handleCheckBoxClick,
                      }}
                    ></ScrollableImageContainer>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </>
      )}
    </Stack>
  )
}

interface ModalComponentProps {}

const ModalComponent: React.FC<ModalComponentProps> = () => {
  const navigate = useNavigate()
  const isShowUploadModal = useSelector(
    (state: RootState) => state.construction.showUploadModal,
  )
  const drawings = useSelector((state: RootState) => state.drawings)
  const files = drawings.files
  const building = useSelector(
    (state: RootState) => state.construction.convertData.building,
  )
  const selectedProject = useSelector(
    (state: RootState) => state.organization.selectedProject,
  )
  const selectedFile = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )
  const convertIssues = useSelector(convertIssuesSelector)
  const convertFailedError = useSelector(convertFailedErrorSelector)
  const isLoading = drawings.isLoading
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [filesUpload, setFilesUpload] = useState(true)
  const [convertTo3D, setConvertTo3D] = useState(false)

  const dispatch = useDispatch<any>()

  const cleanupFunctionsRef = useRef<(() => void)[]>([])

  const isFileLoaded = () => {
    return (
      files.filter((file) => {
        return (
          (!file.pages.length ||
            (file.pages.length && !file.pages[0].isLoading)) &&
          file.s3Filename
        )
      }).length > 0
    )
  }

  const isConvertFailed = () => {
    return (
      convertTo3D && !isLoading && !building.storeys.length && !convertIssues
    )
  }

  const handleConvertTo3DClick = () => {
    dispatch(setIsLoading(true))
    setConvertTo3D(true)
    dispatch(convertTo3DAsync())
  }

  const handleButtonClick = () => {
    dispatch(setIsLoading(true))
    setFilesUpload(false)

    if (drawings.filesType === FileType.Pdf) {
      dispatch(analyzeFilesAsync())
    } else if (drawings.filesType === FileType.Ifc) {
      setConvertTo3D(true)
      dispatch(convertIfcAsync())
    } else {
      setConvertTo3D(true)
      dispatch(convertDxfAsync())
    }
  }

  const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue)
    //setcheckePageId('')
  }

  const backToAnalyzeHandle = () => {
    setFilesUpload(true)
    dispatch(clearAnalyzeResults())
  }

  const backToStartHandle = () => {
    dispatch(clearState())
    setConvertTo3D(false)
    setFilesUpload(true)
  }

  useEffect(() => {
    if (!isShowUploadModal) {
      dispatch(clearState())
    }
  }, [dispatch, isShowUploadModal])

  useEffect(() => {
    if (convertTo3D && building.storeys.length) {
      navigate(`/projects/${selectedProject?.id}/files/${selectedFile?.id}`)
    }
  }, [convertTo3D, building.storeys])

  useEffect(() => {
    return () => {
      dispatch(clearState())
      //dispatch(resetConstructionState())
      dispatch(setIsLoading(false))
    }
  }, [dispatch])

  useEffect(() => {
    return () => {
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup())
    }
  }, [])

  return (
    <Modal
      open={isShowUploadModal}
      onClose={() => {}}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(248, 248, 248, 0.25)',
          }),
        },
      }}
    >
      <>
        <ModalBackground />
        <Box
          sx={(theme) => ({
            zIndex: theme.zIndex.drawer + 1,
            position: 'absolute',
            top: '53%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '67%',
            bgcolor: isLoading ? '#EEEEEE' : 'background.paper',
            border: 'none',
            outline: 'none',
            borderRadius: '20px',
            boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
            padding: '40px',
            height: '90%',
            display: 'flex',
            flexDirection: 'row',
            boxSizing: 'border-box',
          })}
        >
          {!isLoading ? (
            <>
              <Stack
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  width: '25%',
                  gap: 2,
                }}
              >
                <SpacialLogo />
                {!filesUpload && !!!convertIssues && !isConvertFailed() ? (
                  <TabComponent
                    selectedTabIndex={selectedTabIndex}
                    handleChange={handleTabClick}
                  />
                ) : (
                  ''
                )}
              </Stack>

              <Divider flexItem orientation='vertical' />

              {convertTo3D && !!convertIssues ? (
                <UploadConvertIssuesComponent
                  issues={convertIssues}
                  handleConvertTo3DClick={handleConvertTo3DClick}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    pl: '3.3rem',
                    pr: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                    }}
                  >
                    {filesUpload ? (
                      <Typography
                        sx={{
                          color: '#04002E',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                        }}
                      >
                        Upload
                      </Typography>
                    ) : (
                      ''
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#04002E',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: 'normal',
                          }}
                        >
                          Confirm our AI selection
                        </Typography>

                        <Typography
                          sx={{
                            color: '#797979',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: 'normal',
                            maxWidth: '350px',
                          }}
                        >
                          Please ensure we've selected all the corresponding
                          pages for each section before moving forward
                        </Typography>
                      </Box>
                      {!filesUpload && !convertTo3D && (
                        <Button
                          disableRipple
                          size='small'
                          variant='outlined'
                          onClick={backToAnalyzeHandle}
                          sx={{
                            textTransform: 'none',
                            color: '#919191',
                            borderColor: '#919191',
                          }}
                        >
                          Go Back to Analyze
                        </Button>
                      )}
                      {((filesUpload && !!drawings.filesType) ||
                        isConvertFailed()) && (
                        <Button
                          disableRipple
                          size='small'
                          variant='outlined'
                          onClick={backToStartHandle}
                          sx={{
                            textTransform: 'none',
                            color: '#919191',
                            borderColor: '#919191',
                          }}
                        >
                          Go Back
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {filesUpload ? (
                    <Box
                      sx={{
                        gap: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '32px',
                          mt: 2,
                        }}
                      >
                        {icons.map(({ icon, name }) => (
                          <Box
                            key={name}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                            }}
                          >
                            {icon}
                            <Typography
                              sx={{
                                color: '#04002E',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'normal',
                              }}
                            >
                              {name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ height: '100%' }}>
                        <UploadFiles
                          cleanupFunctionsRef={cleanupFunctionsRef}
                        />
                      </Box>
                      <Box
                        sx={{
                          flexShrink: 0,
                          ml: 'auto',
                          alignSelf: 'flex-end',
                        }}
                      >
                        <Button
                          variant='contained'
                          disabled={files.length === 0 || !isFileLoaded()}
                          onClick={handleButtonClick}
                          sx={{
                            background: '#56003D',
                            color: 'white',
                            textTransform: 'capitalize',
                            width: '110px',
                            fontSize: '12px',
                            px: '12px',
                            '&:hover': {
                              backgroundColor: '#56003D',
                            },
                          }}
                        >
                          {drawings.filesType === FileType.Ifc ||
                          drawings.filesType === FileType.Dxf
                            ? 'Convert to 3D'
                            : 'Analyze'}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    // Convert Modal
                    <>
                      <TabContent selectedTabIndex={selectedTabIndex}>
                        <ConvertContent
                          type={DrawingType.Floorplan}
                          isFailed={isConvertFailed()}
                          goBackHandle={backToAnalyzeHandle}
                        />
                        <ConvertContent
                          type={DrawingType.Elevation}
                          isFailed={isConvertFailed()}
                          goBackHandle={backToAnalyzeHandle}
                        />
                      </TabContent>
                      {!isConvertFailed() && (
                        <Box
                          sx={{
                            ml: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            variant='contained'
                            disabled={
                              !drawings.recognizedPages.some(
                                (page) =>
                                  page.isChecked &&
                                  page.drawingType === DrawingType.Floorplan,
                              )
                            }
                            onClick={handleConvertTo3DClick}
                            sx={{
                              background: '#56003D',
                              color: 'white',
                              textTransform: 'capitalize',
                              width: '110px',
                              fontSize: '12px',
                              pl: '10px',
                              pr: '10px',
                              '&:hover': {
                                backgroundColor: '#56003D',
                              },
                            }}
                          >
                            Convert to 3D
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          ) : (
            // Loading screen
            <LoadingComponent
              text={convertTo3D ? 'Converting to 3D' : 'Analyzing'}
            />
          )}
        </Box>
        {convertFailedError && (
          <ConvertFailedModalComponent
            isOpen={!!convertFailedError}
            errorType={convertFailedError?.type}
            handleClose={() => {}}
          />
        )}
      </>
    </Modal>
  )
}

export default ModalComponent
